import { useMemo, useSyncExternalStore } from "react";
import { z } from "zod";

import { changeThroughoutProposalOf, type ChangeThroughoutSelectionProposal, checkablePlacesOf, placesCheckedAsProposed } from "@common/utils/redline/change-throughout-selection";
import { type ChangeThroughoutCard, type ChangeThroughoutMatch, changeThroughoutCardSchema } from "@common/utils/redline/wire/change-throughout";
import type { RedlineTextProposal } from "@common/utils/redline/wire/redline-payload";

import { browserStorage } from "../../../utils/proposal-edits-storage";
import { textRedlineOfToolContent } from "./card-content";

export interface ChangeThroughoutCardSelection extends ChangeThroughoutSelectionProposal {
  card: ChangeThroughoutCard;
}

export interface ChangeThroughoutPlaceEntry {
  ruleId: string;
  place: ChangeThroughoutMatch;
  textRedline: RedlineTextProposal;
}

const CHANGE_THROUGHOUT_SELECTIONS_STORAGE_KEY = "dioptra.changeThroughoutSelections.v1";

const placeOutcomeSchema = z.discriminatedUnion("result", [
  z.object({ result: z.literal("applied") }),
  z.object({ result: z.literal("notApplied"), reason: z.string() }),
  z.object({ result: z.literal("notChosen"), reason: z.string() }),
]);

export type PlaceOutcome = z.infer<typeof placeOutcomeSchema>;

export const NO_PLACE_OUTCOMES: ReadonlyMap<string, PlaceOutcome> = new Map();

const placesCheckedByTheUserSchema = z.object({
  checkablePlaceIds: z.array(z.string()),
  checkedPlaceIds: z.array(z.string()),
  placeOutcomes: z.record(z.string(), placeOutcomeSchema).default({}),
});

const storedSelectionsSchema = z.record(z.string(), placesCheckedByTheUserSchema);

type PlacesCheckedByTheUser = z.infer<typeof placesCheckedByTheUserSchema>;

function checkablePlaceIdsOf(card: ChangeThroughoutCard): string[] {
  return checkablePlacesOf(card).map((checkablePlace) => checkablePlace.match_id);
}

function sameCheckablePlaces(card: ChangeThroughoutCard, placesCheckedByTheUser: PlacesCheckedByTheUser): boolean {
  const cardPlaceIds = checkablePlaceIdsOf(card);
  if (cardPlaceIds.length !== placesCheckedByTheUser.checkablePlaceIds.length) return false;
  const placeIdsTheUserChecked = new Set(placesCheckedByTheUser.checkablePlaceIds);
  return cardPlaceIds.every((cardPlaceId) => placeIdsTheUserChecked.has(cardPlaceId));
}

function storedSelectionsIn(storage: Storage | null): Map<string, PlacesCheckedByTheUser> {
  if (storage === null) return new Map();
  try {
    const raw = storage.getItem(CHANGE_THROUGHOUT_SELECTIONS_STORAGE_KEY);
    if (raw === null) return new Map();
    const storedSelections = storedSelectionsSchema.safeParse(JSON.parse(raw));
    if (!storedSelections.success) return new Map();
    return new Map(Object.entries(storedSelections.data));
  } catch {
    return new Map();
  }
}

function storeSelections(storage: Storage | null, placesCheckedByActionId: ReadonlyMap<string, PlacesCheckedByTheUser>): void {
  if (storage === null) return;
  try {
    storage.setItem(CHANGE_THROUGHOUT_SELECTIONS_STORAGE_KEY, JSON.stringify(Object.fromEntries(placesCheckedByActionId)));
  } catch {
    return;
  }
}

export class ChangeThroughoutSelections {
  private placesCheckedByActionId: Map<string, PlacesCheckedByTheUser> | null = null;
  private readonly listeners = new Set<() => void>();
  private selectionsVersion = 0;

  constructor(private readonly storageOf: () => Storage | null) {}

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  readonly version = (): number => this.selectionsVersion;

  private placesCheckedByTheUser(): Map<string, PlacesCheckedByTheUser> {
    if (this.placesCheckedByActionId === null) this.placesCheckedByActionId = storedSelectionsIn(this.storageOf());
    return this.placesCheckedByActionId;
  }

  private placesCheckedOnThisCard(actionId: string, card: ChangeThroughoutCard): PlacesCheckedByTheUser | null {
    const placesCheckedByTheUser = this.placesCheckedByTheUser().get(actionId);
    if (placesCheckedByTheUser === undefined) return null;
    if (!sameCheckablePlaces(card, placesCheckedByTheUser)) return null;
    return placesCheckedByTheUser;
  }

  checkedPlacesOf(actionId: string, card: ChangeThroughoutCard): ReadonlySet<string> {
    const placesCheckedOnThisCard = this.placesCheckedOnThisCard(actionId, card);
    if (placesCheckedOnThisCard === null) return placesCheckedAsProposed(card);
    return new Set(placesCheckedOnThisCard.checkedPlaceIds);
  }

  placeOutcomesOf(actionId: string, card: ChangeThroughoutCard): ReadonlyMap<string, PlaceOutcome> {
    const placesCheckedOnThisCard = this.placesCheckedOnThisCard(actionId, card);
    if (placesCheckedOnThisCard === null) return NO_PLACE_OUTCOMES;
    return new Map(Object.entries(placesCheckedOnThisCard.placeOutcomes));
  }

  setCheckedPlaces(actionId: string, card: ChangeThroughoutCard, checkedPlaces: ReadonlySet<string>): void {
    const placeOutcomes = Object.fromEntries(this.placeOutcomesOf(actionId, card));
    this.store(actionId, { checkablePlaceIds: checkablePlaceIdsOf(card), checkedPlaceIds: [...checkedPlaces], placeOutcomes });
  }

  recordPlaceOutcomes(actionId: string, card: ChangeThroughoutCard, placeOutcomes: ReadonlyMap<string, PlaceOutcome>): void {
    const placeOutcomesSoFar = Object.fromEntries(this.placeOutcomesOf(actionId, card));
    const checkedPlaceIds = [...this.checkedPlacesOf(actionId, card)];
    this.store(actionId, { checkablePlaceIds: checkablePlaceIdsOf(card), checkedPlaceIds, placeOutcomes: { ...placeOutcomesSoFar, ...Object.fromEntries(placeOutcomes) } });
  }

  private store(actionId: string, placesCheckedByTheUser: PlacesCheckedByTheUser): void {
    const placesCheckedByActionId = this.placesCheckedByTheUser();
    placesCheckedByActionId.set(actionId, placesCheckedByTheUser);
    storeSelections(this.storageOf(), placesCheckedByActionId);
    this.selectionsVersion += 1;
    for (const listener of Array.from(this.listeners)) listener();
  }

  selectionOf(actionId: string, card: ChangeThroughoutCard): ChangeThroughoutCardSelection {
    return { card, ...changeThroughoutProposalOf(card, this.checkedPlacesOf(actionId, card)) };
  }
}

export const changeThroughoutSelections = new ChangeThroughoutSelections(browserStorage);

export function useChangeThroughoutSelectionsVersion(): number {
  return useSyncExternalStore(changeThroughoutSelections.subscribe, changeThroughoutSelections.version, changeThroughoutSelections.version);
}

export function changeThroughoutCardOfToolContent(toolContent: string): ChangeThroughoutCard | null {
  try {
    const parsedCard = changeThroughoutCardSchema.safeParse(JSON.parse(toolContent));
    if (!parsedCard.success) return null;
    return parsedCard.data;
  } catch {
    return null;
  }
}

export function changeThroughoutSelectionOfToolContent(actionId: string, toolContent: string): ChangeThroughoutCardSelection | null {
  const card = changeThroughoutCardOfToolContent(toolContent);
  if (card === null) return null;
  return changeThroughoutSelections.selectionOf(actionId, card);
}

export function useChangeThroughoutSelection(actionId: string, toolContent: string): ChangeThroughoutCardSelection | null {
  const selectionsVersion = useChangeThroughoutSelectionsVersion();
  return useMemo(() => changeThroughoutSelectionOfToolContent(actionId, toolContent), [actionId, toolContent, selectionsVersion]);
}

export function textRedlineOfCardSelection(changeThroughoutSelection: ChangeThroughoutCardSelection | null, toolContent: string): RedlineTextProposal | null {
  if (changeThroughoutSelection === null) return textRedlineOfToolContent(toolContent);
  return changeThroughoutSelection.proposal;
}

export function checksNoPlace(changeThroughoutSelection: ChangeThroughoutCardSelection | null): boolean {
  if (changeThroughoutSelection === null) return false;
  return changeThroughoutSelection.changedMatches.length === 0;
}

export const NO_PLACE_CHECKED_MESSAGE = "Select at least one place to apply this change.";

export function heldBackReasonOfSelection(changeThroughoutSelection: ChangeThroughoutCardSelection | null): string | null {
  if (!checksNoPlace(changeThroughoutSelection)) return null;
  return NO_PLACE_CHECKED_MESSAGE;
}

export function placeRuleIdOf(actionId: string, place: ChangeThroughoutMatch): string {
  return `${actionId}#${place.match_id}`;
}

function placeEntryOf(actionId: string, card: ChangeThroughoutCard, place: ChangeThroughoutMatch): ChangeThroughoutPlaceEntry | null {
  const placeAlone = changeThroughoutProposalOf(card, new Set([place.match_id]));
  if (placeAlone.changedMatches.length === 0) return null;
  const paragraphHoldingThePlace = placeAlone.proposal.paragraphs.filter((cardParagraph) => cardParagraph.bookmark === place.paragraph_id);
  return { ruleId: placeRuleIdOf(actionId, place), place, textRedline: { ...placeAlone.proposal, paragraphs: paragraphHoldingThePlace } };
}

export function placeEntriesOfSelection(actionId: string, changeThroughoutSelection: ChangeThroughoutCardSelection): ChangeThroughoutPlaceEntry[] {
  return changeThroughoutSelection.changedMatches.flatMap((changedPlace) => {
    const placeEntry = placeEntryOf(actionId, changeThroughoutSelection.card, changedPlace);
    if (placeEntry === null) return [];
    return [placeEntry];
  });
}
